import React from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  Link as ChakraLink,
  FormErrorMessage,
  useToast,
} from "@chakra-ui/react";
import { MailCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Formik, Form, Field, FormikHelpers } from "formik";
import * as Yup from "yup";
import { useRequestResetPasswordMutation } from "@state/apiSlice/requests";

// Schema
const ForgotPasswordSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email address").required("Email is required"),
});

export const ForgotPassword = () => {
  const [requestReset] = useRequestResetPasswordMutation();
  const toast = useToast();

  const handleForgotPasswordSubmit = async (values: { email: string }, actions: FormikHelpers<{ email: string }>) => {
    try {
      const { email } = values;

      const result = await requestReset({ email }).unwrap();

      if (result.success) {
        toast({
          title: `Reset requested`,
          description: "If user exists a reset email will be sent",
          status: "success",
          duration: 3000,
          position: "bottom-right",
        });
      }

      actions.setSubmitting(false);
      // Optionally show success toast here
    } catch (err) {
      toast({
        title: `Failed to request reset`,
        description: "Please try again later",
        status: "error",
        duration: 5000,
        position: "bottom-right",
      });

      console.error("Error sending reset instructions", err);
      actions.setSubmitting(false);
    }
  };

  return (
    <Box minH="100vh" display="flex" alignItems="center" justifyContent="center" p={4}>
      <VStack
        spacing={8}
        w="full"
        maxW="400px"
        bg="gray.800"
        p={8}
        borderRadius="lg"
        boxShadow="xl"
        border="1px"
        borderColor="gray.700"
      >
        <Heading size="lg" color="white">
          Reset Password
        </Heading>
        <Text color="gray.400" textAlign="center">
          Enter your email address and we'll send you instructions to reset your password.
        </Text>

        <Formik initialValues={{ email: "" }} validationSchema={ForgotPasswordSchema} onSubmit={handleForgotPasswordSubmit}>
          {({ isSubmitting }) => (
            <Form style={{ width: "100%" }}>
              <VStack spacing={4} w="full">
                <Field name="email">
                  {({ field, form }: any) => (
                    <FormControl isInvalid={form.errors.email && form.touched.email}>
                      <FormLabel color="gray.300">Email</FormLabel>
                      <Input {...field} type="email" placeholder="your@email.com" bg="gray.700" border="none" color="white" />
                      <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                <Button
                  type="submit"
                  w="full"
                  bg="#ff3d00"
                  color="white"
                  leftIcon={<MailCheck size={20} />}
                  isLoading={isSubmitting}
                  loadingText="Sending..."
                  _hover={{
                    bg: "#e53700",
                    boxShadow: "0 0 20px rgba(255, 61, 0, 0.5)",
                  }}
                >
                  Send Reset Instructions
                </Button>
                <Text color="gray.400">
                  Remember your password?{" "}
                  <ChakraLink as={Link} to="/ap/login" color="cyan.400" _hover={{ color: "cyan.300" }}>
                    Back to login
                  </ChakraLink>
                </Text>
              </VStack>
            </Form>
          )}
        </Formik>
      </VStack>
    </Box>
  );
};
